import type { ICreateAddressForm, ICreateContactForm } from '@/types/core/forms'
import { type UploadFile } from 'element-plus'
import type { ContainerStatus, INewContainer } from '../containers'
import type { IWorkOrderDashboardMinersFilters } from '../filters'
import type { IFan, IHashboard, IMinerEntryCommons, IMinerIdentifier, IModel } from '../miners'
import type { MinerStatus } from '../miners/MinerStatus'
import type { PartType } from '../miners/PartType'
import type { SparePartStatus } from '../spare-parts'
import type { IDamageInstance, ShipmentType } from '../shipments'
import type { WorkOrderStatus } from '../work-orders/WorkOrderStatus'
import type { WorkOrderPriority } from '../work-orders/WorkOrderPriority'

/**
 * Create Customer
 */
export interface CustomerForm {
  alias: string
  email: string
  phone: string
  first_name: string
  last_name: string
  company_name: string
  billing_address?: ICreateAddressForm | null
  billing_contact?: ICreateContactForm | null
  corporate_address?: ICreateAddressForm | null
  operations_contact?: ICreateContactForm | null
}

/**
 * Update TOS acceptance
 */
export interface ILegalDocumentsAcceptanceForm {
  tos_accepted?: boolean
  privacy_policy_accepted?: boolean
  eula_accepted?: boolean
}

export enum LegalDocumentType {
  TOS = 'Terms of Service',
  PP = 'Privacy Policy',
  EULA = 'End User License Agreement',
}

export interface ILegalDocument {
  id: number
  document_url: string
  document_name: LegalDocumentType
  version: number
  active: boolean
  created_at: string
}

/**
 * Create/Update RSA
 */
export interface IRSAForm {
  start_date?: Date
  months_term: number
  signed_document?: UploadFile
}

export interface ICreateRSAForm extends IRSAForm {
  customer_id: number
}

export interface ICreateRSAVersionForm extends IRSAForm {
  rsa_number: string
}

/**
 * Create Shipment (Inbound/Outbound)
 */
export interface IShipmentFormTrackingInformation {
  carrier?: string | null
  tracking_number?: string | null
  bol_number?: string | null
  waybill_number?: string | null
}

interface IShipmentForm {
  id?: string
  work_order?: {
    number: string
    customer?: string
  }
  tracking_information: IShipmentFormTrackingInformation
  expected_containers: string
  expected_miners: string
  expected_container_types: string[]
  receipts?: UploadFile[]
  deleted_receipt_ids?: number[] // Used for updates
  delivered_date?: string
  picked_up_date?: string
}

export interface IShipmentFormInbound extends IShipmentForm {
  customer_drop_off?: boolean
}

export interface IShipmentFormOutbound extends IShipmentForm {
  customer_pick_up?: boolean
}

export interface IUpdateRepairSubmissionStatusForm {
  status: string
  reason: string | undefined
}

export interface IMinerPartUpdateForm extends Partial<IMinerEntryCommons> {
  id: string
  hashboards?: Partial<IHashboard>[]
  fans?: Partial<IFan>[]
}

export interface IMinerPartDeleteForm {
  part_id: number
  part_type: PartType
}

export interface IMinerPartMoveToInventoryForm {
  part_id: number
  part_type: PartType
}

export interface IMinerPartAllocationForm {
  spare_part_id: number
  part_type: PartType
  part_number?: number
}

/**
 * Create/Update Container
 */
export interface IContainerForm {
  id?: number
  kind: ShipmentType
  work_order:
    | {
        number: string
        customer?: string
      }
    | undefined
  customer?: {
    id: number
    name: string
  }
  shipment?: number | null // Send null to clear
  container_number: string
  container_type: string
  status: ContainerStatus
  miners: IMinerIdentifier[]
  miners_expected: number
  damages: IDamageInstance[]
  length?: number
  width?: number
  height?: number
  weight?: number
}

export interface IContainerCreationForm extends IContainerForm {} // eslint-disable-line @typescript-eslint/no-empty-object-type

export interface IAddBulkContainersToShipmentForm {
  shipment_id: string
  shipment_kind: ShipmentType
  containers: INewContainer[]
  damages: IDamageInstance[]
}

export interface IContainerUpdateForm {
  container_number?: string
  container_type?: string
  miners_expected?: number
  newDamages?: IDamageInstance[]
  deletedDamages?: IDamageInstance[]
  shipment?: number | null
  miner_ids?: number[]
  status?: ContainerStatus
  length?: number
  width?: number
  height?: number
  weight?: number
}

export interface IContainerStatusUpdate {
  id: number
  status: string
}

/* Spare Parts */
export interface ISparePartCreationForm {
  site_id: string
  part_type: PartType
  customer_id: number | null
  part_sn?: string
  part_model?: IModel
  price?: number | null
}

export type ISparePartUpdateForm = Partial<ISparePartCreationForm> & {
  id: number
  status?: SparePartStatus
}

export interface ISparePartBulkUpdateForm {
  status?: SparePartStatus
  customer_id?: number | null
  site_id?: string
  price?: number | null
}

export interface IWorkOrderUpdateForm {
  site_id?: string
  status?: WorkOrderStatus
  priority?: WorkOrderPriority | null
  notes?: string
}

export interface IServiceForm {
  description: string
  icon: string
  price?: number
}

export interface IRepairActionForm {
  part_type: string
  action: string
  price?: number
}

export interface IBulkUpdateMinersForm {
  work_order_number: string
  miner_ids?: number[]
  miner_filters?: IWorkOrderDashboardMinersFilters
  status?: MinerStatus
  completed_services?: number[]
}

export interface IWorkOrderChatMessageForm {
  message: string
}

export interface ICustomerRepairRequestSubmissionForm {
  tenant_id: string
  scope_of_work: number[]
  expected_units: number
  miner_models: string[]
  notes?: string
}
